import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { TappableCard, SlideTransition } from "@/utils/animations";
import { PetAvatar } from "@/components/PetAvatar";
import { DoodleStar, DoodlePencil, DoodleHeart } from "@/components/Doodles";
import { PET_TYPES } from "@/constants/petConstants";

// ── Shared design tokens ───────────────────────────────────────
const S = {
  card:       { background: "#FFFFFF", borderRadius: 4, padding: 12, border: "1.5px dashed #CCCCCC" },
  cardActive: { background: "#FFFFFF", borderRadius: 4, padding: 12, border: "2px solid #448AFF" },
  btn:        { background: "#448AFF", borderRadius: 12, padding: "12px 20px",
                color: "#fff", fontWeight: 400, fontSize: 16, border: "none", width: "100%", cursor: "pointer",
                fontFamily: "'Patrick Hand', cursive" },
  heading:    { fontSize: 28, fontWeight: 400, fontFamily: "'Patrick Hand', cursive", color: "#1A1A2E", textAlign: "center", margin: "24px 0 4px" },
  sub:        { fontSize: 15, color: "#4A4A5A", textAlign: "center", marginBottom: 16 },
  section:    { marginTop: 12 },
};

const CLASS_DATA = [
  { label: "Class 1", value: "1", ages: "Age 6",     desc: "Simple stories",  group: "class_1_2" },
  { label: "Class 2", value: "2", ages: "Age 7",     desc: "Easy adventures", group: "class_1_2" },
  { label: "Class 3", value: "3", ages: "Age 8",     desc: "Fun stories",     group: "class_3_4" },
  { label: "Class 4", value: "4", ages: "Age 9",     desc: "Cool adventures", group: "class_3_4" },
  { label: "Class 5", value: "5", ages: "Age 10",    desc: "Deep concepts",   group: "class_5_6" },
  { label: "Class 6", value: "6", ages: "Age 11",    desc: "Big concepts",    group: "class_5_6" },
  { label: "Class 7", value: "7", ages: "Age 12",    desc: "Advanced topics", group: "class_7_8" },
  { label: "Class 8", value: "8", ages: "Age 13-14", desc: "Expert level",    group: "class_7_8" },
];

const BOARD_DATA = [
  { emoji: "📗", label: "CBSE / NCERT",  value: "cbse",   desc: "Central Board of Secondary Education" },
  { emoji: "📙", label: "Telangana SSC", value: "ts_ssc", desc: "TS SCERT State Board" },
  { emoji: "📕", label: "ICSE",          value: "icse",   desc: "CISCE Board" },
  { emoji: "📘", label: "International Baccalaureate (IB)", value: "ib", desc: "IB PYP & MYP Programme" },
];

// ── Shared sub-components ──────────────────────────────────────
const YouLogo = () => (
  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
    <img src="/logo-book.png" alt="YOU" style={{ width: 44, height: 44, objectFit: 'contain' }} />
    <div>
      <img src="/logo-you-text.png" alt="YOU" style={{ height: 14, objectFit: 'contain', display: 'block' }} />
      <img src="/logo-subtitle.png" alt="Your Own Understanding" style={{ height: 8, objectFit: 'contain', display: 'block', marginTop: 2 }} />
    </div>
  </div>
);

const ProgressDots = ({ currentStep }) => (
  <div style={{ display: "flex", gap: 6, justifyContent: "center", marginBottom: 16 }}>
    {[0, 1, 2, 3, 4].map(i => (
      <div key={i} style={{
        height: 6, borderRadius: 3,
        width: i === currentStep ? 20 : 6,
        background: i === currentStep ? "#448AFF" : "rgba(0,0,0,0.08)",
        transition: "all 0.3s ease",
      }} />
    ))}
  </div>
);

const GradientBtn = ({ onClick, disabled, children, style = {} }) => (
  <button onClick={onClick} disabled={disabled}
    style={{ ...S.btn, opacity: disabled ? 0.5 : 1, ...style }}>
    {children}
  </button>
);

// ── Step 0: Who Are You ────────────────────────────────────────
const StepWho = ({ selections, update, onNext }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 8, position: "relative" }}>
    <div style={{ position: "absolute", top: 8, right: 8, pointerEvents: "none" }}>
      <DoodleStar color="#448AFF" opacity={0.2} size={12} />
    </div>
    <YouLogo />
    <ProgressDots currentStep={0} />
    <div style={S.heading}>Hey! Who are you? 👋</div>
    <div style={S.sub}>Learn together with your own AI storyteller</div>
    <div style={{ display: "flex", gap: 8 }}>
      {[
        { role: "student", emoji: "🎒", label: "Student", sub: "I want to learn!" },
        { role: "parent",  emoji: "👨‍👩‍👧", label: "Parent",  sub: "Track my child"  },
      ].map(({ role, emoji, label, sub }) => (
        <TappableCard key={role} onClick={() => update("role", role)}
          style={{ ...(selections.role === role ? S.cardActive : S.card), flex: 1, textAlign: "center" }}>
          <div style={{ fontSize: 28 }}>{emoji}</div>
          <div style={{ fontSize: 16, fontWeight: 500, color: selections.role === role ? "#448AFF" : "#1A1A2E" }}>{label}</div>
          <div style={{ fontSize: 11, color: "#4A4A5A" }}>{sub}</div>
        </TappableCard>
      ))}
    </div>
    <div style={{ ...S.card, ...S.section, borderLeft: "3px solid #66BB6A" }}>
      <div style={{ fontSize: 16, fontWeight: 400, fontFamily: "'Patrick Hand', cursive", color: "#448AFF", marginBottom: 8 }}>What happens next:</div>
      {["📖 Pick class & board", "🐾 Choose your LearnPet", "🎧 Any topic → story", "⭐ Stars & evolution"].map(row => (
        <div key={row} style={{ fontSize: 13, color: "#4A4A5A", marginBottom: 4 }}>{row}</div>
      ))}
    </div>
    <div style={{ fontSize: 11, color: "#4A4A5A", textAlign: "center", ...S.section }}>
      🏫 CBSE · TS SSC · ICSE · IB
    </div>
    <div style={S.section}>
      <GradientBtn onClick={onNext}>Continue →</GradientBtn>
    </div>
  </div>
);

// ── Step 1: Class Selection ────────────────────────────────────
const StepClass = ({ selections, update, onNext }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
    <YouLogo />
    <ProgressDots currentStep={1} />
    <div style={S.heading}>Which class are you in?</div>
    <div style={S.sub}>Priya adjusts the story for your level</div>
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
      {CLASS_DATA.map(({ label, value, ages, desc }) => {
        const active = selections.classGroup === value;
        return (
          <TappableCard key={value} onClick={() => update("classGroup", value)}
            style={{ ...(active ? S.cardActive : S.card), textAlign: "center" }}>
            <div style={{ fontSize: 28, fontWeight: 400, fontFamily: "'Patrick Hand', cursive", color: "#448AFF" }}>{label}</div>
            <div style={{ fontSize: 12, color: "#4A4A5A" }}>{ages}</div>
            <div style={{ fontSize: 12, color: "#4A4A5A" }}>{desc}</div>
          </TappableCard>
        );
      })}
    </div>
    <div style={{ ...S.card, ...S.section }}>
      <div style={{ fontSize: 12, color: "#4A4A5A", textAlign: "center" }}>
        Maths · Science · Social · History · Geography · English
      </div>
    </div>
    <div style={S.section}>
      <GradientBtn onClick={onNext} disabled={!selections.classGroup}>Continue →</GradientBtn>
    </div>
  </div>
);

// ── Step 2: Board Selection ────────────────────────────────────
const StepBoard = ({ selections, update, onNext }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
    <YouLogo />
    <ProgressDots currentStep={2} />
    <div style={S.heading}>Choose your board</div>
    <div style={S.sub}>Topics will match your exact syllabus</div>
    {BOARD_DATA.map(({ emoji, label, value, desc }) => {
      const active = selections.board === value;
      return (
        <TappableCard key={value} onClick={() => update("board", value)}
          style={{ ...(active ? S.cardActive : S.card), display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ fontSize: 18 }}>{emoji}</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 16, fontWeight: 500, color: active ? "#448AFF" : "#1A1A2E" }}>{label}</div>
            <div style={{ fontSize: 13, color: "#4A4A5A" }}>{desc}</div>
          </div>
          <div style={{
            width: 16, height: 16, borderRadius: "50%",
            border: `2px solid ${active ? "#448AFF" : "rgba(0,0,0,0.2)"}`,
            background: active ? "#448AFF" : "transparent",
            flexShrink: 0,
          }} />
        </TappableCard>
      );
    })}
    <div style={S.section}>
      <GradientBtn onClick={onNext} disabled={!selections.board}>Continue →</GradientBtn>
    </div>
  </div>
);

// ── Step 3: Pet Selection (Carousel Flow) ──────────────────────

// Sub-screen A: Hatching egg
const EggHatch = ({ onHatched }) => {
  const [phase, setPhase] = useState('whole'); // whole → cracking → burst
  useEffect(() => {
    const t1 = setTimeout(() => setPhase('cracking'), 600);
    const t2 = setTimeout(() => setPhase('burst'), 2000);
    const t3 = setTimeout(() => onHatched(), 2400);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [onHatched]);

  const eggAnimate = phase === 'cracking'
    ? { rotate: [-3, 5, -5, 4, -3, 0], scale: [1, 1.03, 1] }
    : phase === 'burst'
    ? { scale: [1, 1.4, 1.5], opacity: [1, 1, 0] }
    : { scale: [1, 1.02, 1] };
  const eggTransition = phase === 'cracking'
    ? { duration: 0.6, repeat: Infinity, ease: 'easeInOut' }
    : phase === 'burst'
    ? { duration: 0.4, ease: 'easeOut' }
    : { duration: 2, repeat: Infinity, ease: 'easeInOut' };

  return (
    <div style={{
      display: "flex", flexDirection: "column", alignItems: "center",
      justifyContent: "center", minHeight: 420,
      background: "#FFFEF9",
      borderRadius: 4, padding: 32,
    }}>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        style={{ fontSize: 14, fontFamily: "'Patrick Hand', cursive", color: "#555555", marginBottom: 32, letterSpacing: 2 }}
      >
        Something is hatching...
      </motion.div>

      {/* Egg SVG */}
      <motion.div animate={eggAnimate} transition={eggTransition}>
        <svg width="120" height="140" viewBox="0 0 120 140" xmlns="http://www.w3.org/2000/svg">
          {/* Egg body */}
          <ellipse cx="60" cy="78" rx="48" ry="58" fill="#FFF8E8"/>
          <ellipse cx="60" cy="78" rx="44" ry="54" fill="#FFFCF0"/>
          {/* Crack lines (visible when cracking) */}
          {phase === 'cracking' && (
            <>
              <path d="M60 30 L55 48 L65 56 L58 70" fill="none" stroke="#D4C088" strokeWidth="2" strokeLinecap="round"/>
              <path d="M40 60 L52 54 L48 68" fill="none" stroke="#D4C088" strokeWidth="1.5" strokeLinecap="round"/>
              <path d="M80 65 L70 58 L74 72" fill="none" stroke="#D4C088" strokeWidth="1.5" strokeLinecap="round"/>
              {/* Glow from inside */}
              <ellipse cx="60" cy="78" rx="48" ry="58" fill="rgba(255,184,0,0.12)"/>
            </>
          )}
          {/* Star sparkles */}
          {phase === 'cracking' && [
            [30,35], [90,40], [20,80], [100,72], [50,20],
          ].map(([x, y], i) => (
            <circle key={i} cx={x} cy={y} r="3" fill="#FFD700" opacity={0.7}/>
          ))}
        </svg>
      </motion.div>

      <AnimatePresence>
        {phase === 'burst' && (
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: [0, 1.4, 1], opacity: 1 }}
            transition={{ duration: 0.35, ease: 'backOut' }}
            style={{ fontSize: 48 }}
          >✨</motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Sub-screen B: Carousel
const PetCarousel = ({ selectedType, onSelect, onChoose }) => {
  const [activeIdx, setActiveIdx] = useState(0);
  const scrollRef = useRef(null);

  const handleScroll = () => {
    if (!scrollRef.current) return;
    const el = scrollRef.current;
    const cardW = el.offsetWidth;
    const idx = Math.round(el.scrollLeft / cardW);
    setActiveIdx(idx);
  };

  const scrollTo = (idx) => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTo({ left: idx * scrollRef.current.offsetWidth, behavior: 'smooth' });
  };

  const activePet = PET_TYPES[activeIdx];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
      <div style={{ textAlign: "center", marginBottom: 12 }}>
        <div style={S.heading}>Meet your study buddies! 🐾</div>
        <div style={S.sub}>Swipe to meet them all</div>
      </div>

      {/* Carousel with arrow buttons */}
      <div style={{ position: "relative" }}>
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          style={{
            display: "flex",
            overflowX: "auto",
            scrollSnapType: "x mandatory",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            WebkitOverflowScrolling: "touch",
            borderRadius: 4,
          }}
        >
          {PET_TYPES.map((pet, idx) => {
            const isActive = idx === activeIdx;
            return (
              <div
                key={pet.id}
                onClick={() => { scrollTo(idx); }}
                style={{
                  minWidth: "100%",
                  scrollSnapAlign: "center",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  padding: "24px 16px 20px",
                  background: `${pet.color}11`,
                  borderRadius: 4,
                  border: `1.5px dashed ${pet.color}55`,
                  transition: "transform 0.3s ease",
                  transform: isActive ? "scale(1)" : "scale(0.9)",
                }}
              >
                {/* Pet avatar */}
                <div style={{
                  background: `${pet.color}22`,
                  borderRadius: "50%",
                  padding: 16,
                  marginBottom: 12,
                }}>
                  <PetAvatar
                    petType={pet.id}
                    stage={1}
                    mood={selectedType === pet.id ? 'excited' : 'happy'}
                    size="xl"
                    animated={true}
                  />
                </div>

                <div style={{ fontSize: 26, fontWeight: 500, color: "#1A1A2E", marginBottom: 4 }}>
                  {pet.species}
                </div>
                <div style={{ fontSize: 16, color: "#4A4A5A", textAlign: "center", marginBottom: 6 }}>
                  {pet.tagline}
                </div>
                <div style={{
                  fontSize: 11, color: pet.color, fontWeight: 500,
                  background: `${pet.color}22`, borderRadius: 20,
                  padding: "3px 12px",
                }}>
                  Best for: {pet.appealsTo}
                </div>
              </div>
            );
          })}
        </div>

        {/* Left arrow */}
        {activeIdx > 0 && (
          <button
            onClick={() => scrollTo(activeIdx - 1)}
            style={{
              position: "absolute", left: -4, top: "50%", transform: "translateY(-50%)",
              width: 40, height: 40, borderRadius: "50%",
              background: "#fff", border: "none",
              boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
              fontSize: 22, color: "#448AFF", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              padding: 0, zIndex: 2,
            }}
            aria-label="Previous pet"
          >
            ‹
          </button>
        )}

        {/* Right arrow */}
        {activeIdx < PET_TYPES.length - 1 && (
          <button
            onClick={() => scrollTo(activeIdx + 1)}
            style={{
              position: "absolute", right: -4, top: "50%", transform: "translateY(-50%)",
              width: 40, height: 40, borderRadius: "50%",
              background: "#fff", border: "none",
              boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
              fontSize: 22, color: "#448AFF", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              padding: 0, zIndex: 2,
            }}
            aria-label="Next pet"
          >
            ›
          </button>
        )}
      </div>

      {/* Dot indicators */}
      <div style={{ display: "flex", justifyContent: "center", gap: 6, marginTop: 12 }}>
        {PET_TYPES.map((_, i) => (
          <div
            key={i}
            onClick={() => scrollTo(i)}
            style={{
              width: i === activeIdx ? 20 : 7,
              height: 7,
              borderRadius: 4,
              background: i === activeIdx ? "#448AFF" : "rgba(0,0,0,0.15)",
              transition: "all 0.3s ease",
              cursor: "pointer",
            }}
          />
        ))}
      </div>

      <div style={{ marginTop: 16 }}>
        <GradientBtn onClick={() => onChoose(activePet)}>
          Choose {activePet?.species}! ✨
        </GradientBtn>
      </div>
    </div>
  );
};

// Sub-screen C: Connection moment
const ConnectionMoment = ({ pet, onConfirm, onBack }) => {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }}>
      <div style={S.heading}>✨ It's a match!</div>

      <div style={{
        background: `${pet.color}22`,
        borderRadius: "50%", padding: 24, position: "relative",
      }}>
        <PetAvatar petType={pet.id} stage={1} mood="celebrating" size="xl" animated={true}/>
        {/* Heart particles */}
        {['❤️','💕','💖','💗','💓'].map((h, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 0, x: 0 }}
            animate={{ opacity: [0, 1, 0], y: [-10, -60 - i * 10], x: i % 2 === 0 ? -20 : 20 }}
            transition={{ duration: 1.2 + i * 0.25, repeat: Infinity, delay: i * 0.2, ease: 'easeOut' }}
            style={{
              position: "absolute",
              top: `${30 + i * 8}%`,
              left: i % 2 === 0 ? '-10px' : '100%',
              fontSize: 18,
            }}
          >{h}</motion.div>
        ))}
      </div>

      <div style={{ fontSize: 16, fontWeight: 500, color: "#1A1A2E", textAlign: "center" }}>
        Looks like the {pet.species} really likes you!
      </div>
      <div style={{ fontSize: 16, color: "#4A4A5A", textAlign: "center" }}>
        {pet.personality}
      </div>

      <div style={{ width: "100%" }}>
        <GradientBtn onClick={onConfirm}>
          Yes, this is my buddy! 🎉
        </GradientBtn>
      </div>
      <button onClick={onBack} style={{
        background: "none", border: "none", color: "#448AFF",
        fontSize: 16, cursor: "pointer", textDecoration: "underline",
      }}>
        Wait, let me look again
      </button>
    </div>
  );
};

// Sub-screen D: Naming + Promise
const NamingScreen = ({ pet, petName, onNameChange, onConfirm }) => {
  const [inputMood, setInputMood] = useState('happy');
  const [showPromise, setShowPromise] = useState(false);

  const handleChange = (e) => {
    const val = e.target.value.slice(0, 15);
    onNameChange(val);
    if (val.length === 1) setInputMood('excited');
    else if (val.length >= 2) setInputMood('celebrating');
    else setInputMood('happy');
  };

  const handleConfirm = () => {
    setShowPromise(true);
    setTimeout(() => onConfirm(), 100);
  };

  const starsTotal = 20;

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
      {!showPromise ? (
        <>
          <div style={S.heading}>What's their name? 💛</div>
          <div style={S.sub}>Give your new buddy a name they'll love</div>

          <div style={{
            background: `${pet.color}22`,
            borderRadius: "50%", padding: 16,
          }}>
            <PetAvatar petType={pet.id} stage={1} mood={inputMood} size="lg" animated={true}/>
          </div>

          {petName && (
            <div style={{
              background: "#FFD740", color: "#1A1A2E",
              borderRadius: 20, padding: "4px 16px",
              fontSize: 16, fontWeight: 500,
            }}>
              {petName} ⭐
            </div>
          )}

          <input
            type="text"
            value={petName}
            onChange={handleChange}
            placeholder="Type a name..."
            maxLength={15}
            autoFocus
            style={{
              width: "80%",
              borderBottom: "2px solid #448AFF", borderTop: "none",
              borderLeft: "none", borderRight: "none",
              background: "transparent",
              fontSize: 20, textAlign: "center", color: "#1A1A2E",
              padding: "8px 0", outline: "none",
            }}
          />
          <div style={{ fontSize: 11, color: "#B0B0C0" }}>{petName.length}/15 characters</div>

          <div style={{ width: "100%", marginTop: 8 }}>
            <GradientBtn onClick={handleConfirm} disabled={!petName.trim()}>
              Let's go! 🎉
            </GradientBtn>
          </div>
        </>
      ) : (
        <>
          <div style={S.heading}>Welcome, {petName}! 🌟</div>

          <div style={{ position: "relative" }}>
            <PetAvatar petType={pet.id} stage={1} mood="celebrating" size="lg" animated={true}/>
            <div style={{
              position: "absolute", top: -12, left: "50%", transform: "translateX(-50%)",
              background: "#FFD740", color: "#1A1A2E",
              borderRadius: 20, padding: "3px 14px",
              fontSize: 16, fontWeight: 500, whiteSpace: "nowrap",
            }}>
              {petName} ⭐
            </div>
          </div>

          {/* Speech bubble */}
          <div style={{
            ...S.card,
            background: `${pet.color}22`,
            border: `1px solid ${pet.color}44`,
            position: "relative", marginTop: 8,
            fontSize: 16, color: "#1A1A2E", lineHeight: 1.6, textAlign: "center",
          }}>
            <div style={{
              position: "absolute", top: -8, left: "50%", transform: "translateX(-50%)",
              width: 0, height: 0,
              borderLeft: "8px solid transparent", borderRight: "8px solid transparent",
              borderBottom: `8px solid ${pet.color}44`,
            }}/>
            "{petName} promises to learn with you every day!
            But {petName} needs YOU to come back and teach new things.
            Together we'll grow stronger! 🌟"
          </div>

          {/* Progress ring (empty) */}
          <div style={{ textAlign: "center" }}>
            <svg width="80" height="80" viewBox="0 0 80 80">
              <circle cx="40" cy="40" r="32" fill="none" stroke="rgba(0,0,0,0.08)" strokeWidth="6"/>
              <circle cx="40" cy="40" r="32" fill="none" stroke="#448AFF" strokeWidth="6"
                strokeDasharray={`${2 * Math.PI * 32}`}
                strokeDashoffset={`${2 * Math.PI * 32}`}
                strokeLinecap="round"
                style={{ transform: "rotate(-90deg)", transformOrigin: "center" }}
              />
              <text x="40" y="44" textAnchor="middle" fontSize="15" fontWeight="500" fill="#448AFF">
                0/{starsTotal}⭐
              </text>
            </svg>
            <div style={{ fontSize: 11, color: "#4A4A5A", marginTop: 4 }}>Stars to first evolution</div>
          </div>
        </>
      )}
    </div>
  );
};

// ── StepPet: orchestrates A → B → C → D ───────────────────────
const StepPet = ({ selections, update, onNext }) => {
  const [subScreen, setSubScreen] = useState('egg'); // egg | carousel | connection | naming
  const [chosenPet, setChosenPet] = useState(null);

  const handleHatched = () => setSubScreen('carousel');
  const handleChoose = (pet) => {
    setChosenPet(pet);
    update("petType", pet.id);
    setSubScreen('connection');
  };
  const handleConfirm = () => setSubScreen('naming');
  const handleBack = () => setSubScreen('carousel');
  const handleNamingConfirm = () => {
    // Small delay so promise screen shows briefly
    setTimeout(onNext, 1800);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <YouLogo />
      <ProgressDots currentStep={3} />

      {subScreen === 'egg' && <EggHatch onHatched={handleHatched}/>}
      {subScreen === 'carousel' && (
        <PetCarousel
          selectedType={selections.petType}
          onSelect={(id) => update("petType", id)}
          onChoose={handleChoose}
        />
      )}
      {subScreen === 'connection' && chosenPet && (
        <ConnectionMoment pet={chosenPet} onConfirm={handleConfirm} onBack={handleBack}/>
      )}
      {subScreen === 'naming' && chosenPet && (
        <NamingScreen
          pet={chosenPet}
          petName={selections.petName}
          onNameChange={(v) => update("petName", v)}
          onConfirm={handleNamingConfirm}
        />
      )}
    </div>
  );
};

// ── Step 4: Name Capture ───────────────────────────────────────
const StepName = ({ selections, update, onComplete }) => {
  const classLabel = CLASS_DATA.find(c => c.value === selections.classGroup)?.label ?? `Class ${selections.classGroup}`;
  const boardLabel = BOARD_DATA.find(b => b.value === selections.board)?.label ?? selections.board;

  const handleComplete = () => {
    const classNum = selections.classGroup;
    const groupMap = {
      "1": "class_1_2", "2": "class_1_2",
      "3": "class_3_4", "4": "class_3_4",
      "5": "class_5_6", "6": "class_5_6",
      "7": "class_7_8", "8": "class_7_8",
    };
    localStorage.setItem("you_role",                selections.role);
    localStorage.setItem("you_class_number",        classNum);
    localStorage.setItem("you_class",               groupMap[classNum] || "class_5_6");
    localStorage.setItem("you_board",               selections.board);
    localStorage.setItem("you_pet_type",            selections.petType);
    localStorage.setItem("you_pet_name",            selections.petName);
    localStorage.setItem("you_pet_stars",           "0");
    localStorage.setItem("you_pet_stage",           "1");
    localStorage.setItem("you_display_name",        selections.displayName);
    localStorage.setItem("you_onboarding_complete", "true");
    onComplete();
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <YouLogo />
      <ProgressDots currentStep={4} />
      <div style={{ display: "flex", justifyContent: "center", marginTop: 8 }}>
        <PetAvatar petType={selections.petType} stage={1} mood="happy" size="md" animated/>
      </div>
      <div style={{ fontSize: 15, color: "#4A4A5A", textAlign: "center", marginTop: 4 }}>
        {selections.petName || "Your pet"} wants to know your name!
      </div>
      <div style={S.heading}>What should we call you?</div>
      <div style={{ display: "flex", justifyContent: "center" }}>
        <input
          type="text"
          value={selections.displayName}
          onChange={e => update("displayName", e.target.value)}
          placeholder="Your name..."
          style={{
            width: "80%", background: "transparent",
            borderBottom: "2px solid #448AFF", borderTop: "none",
            borderLeft: "none", borderRight: "none",
            color: "#1A1A2E", fontSize: 24, textAlign: "center",
            padding: 8, outline: "none",
          }}
        />
      </div>
      <div style={{ ...S.card, ...S.section }}>
        <div style={{ fontSize: 16, color: "#4A4A5A" }}>
          Hi {selections.displayName || "friend"}! I'm {selections.petName || "your pet"}. Let's learn together! 🎉
        </div>
      </div>
      <div style={{ fontSize: 12, color: "#4A4A5A", textAlign: "center" }}>
        {classLabel} · {boardLabel} · {selections.petName} the {PET_TYPES.find(p => p.id === selections.petType)?.species || selections.petType}
      </div>
      <div style={S.section}>
        <GradientBtn onClick={handleComplete} disabled={!selections.displayName.trim()}>
          Let's go, {selections.petName || "buddy"}! →
        </GradientBtn>
      </div>
      <div style={{ fontSize: 12, color: "#4A4A5A", textAlign: "center", marginTop: 4 }}>
        No account needed — try 2 free stories!
      </div>
    </div>
  );
};

// ── Main Onboarding component ──────────────────────────────────
export default function Onboarding({ onComplete }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [selections, setSelections] = useState({
    role:        "student",
    classGroup:  "",
    board:       "",
    petType:     "elephant",
    petName:     "",
    displayName: "",
  });

  const update = (key, value) =>
    setSelections(prev => ({ ...prev, [key]: value }));

  const next = () => setCurrentStep(s => s + 1);

  const stepContent = (() => {
    switch (currentStep) {
      case 0: return <StepWho   selections={selections} update={update} onNext={next} />;
      case 1: return <StepClass selections={selections} update={update} onNext={next} />;
      case 2: return <StepBoard selections={selections} update={update} onNext={next} />;
      case 3: return <StepPet   selections={selections} update={update} onNext={next} />;
      case 4: return <StepName  selections={selections} update={update} onComplete={onComplete} />;
      default: return null;
    }
  })();

  return (
    <div style={{
      position: "fixed", inset: 0,
      background: "#FFFEF9",
      overflowY: "auto",
      zIndex: 9999,
    }}>
      <div style={{ maxWidth: 720, margin: "0 auto", padding: "24px 16px 48px" }}>
        <SlideTransition key={currentStep} direction="right">
          {stepContent}
        </SlideTransition>
      </div>
    </div>
  );
}
